'use client'

import React, { createContext, useState, useContext, useEffect } from 'react';
import { restFeathers, socketFeathers } from '@/lib/feathersClient';
import { OrganizationPermissions, UserRole, hasUnrestrictedAccess } from '@/types/permissions';

interface VoiceAgent {
  id: number;
  agent_extension: string;
  sip_uri: string | null;
  telnyx_username: string | null;
  telnyx_password: string | null;
  status: 'active' | 'inactive' | 'busy' | 'unavailable';
  is_available: boolean;
  ring_timeout_seconds: number;
  weight_percentage: number;
}

interface Organization {
  id: number;
  name: string;
  role: UserRole;
  subscription_status: 'active' | 'canceled' | 'trialing' | null;
  subscription_plan_name: string;
  timezone?: string;
  logo_url?: string | null;
  default_locale?: 'en' | 'es';
  default_phone_country_iso?: string;
  default_phone_country_code?: string;
  channels: {
    instagram: boolean;
    whatsapp: boolean;
    messenger: boolean;
    voice?: boolean;
  };
  voiceAgent?: VoiceAgent | null;
  permissions?: OrganizationPermissions;
  rate_limit_enabled?: boolean;
  rate_limit_max_requests?: number;
  rate_limit_window_seconds?: number;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  googleId: string | null;
  phone_country_code: string;
  phone: string;
  role: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  organizations: Organization[];
  avatar_url?: string | null;
  preference_conversations_pipeline_view?: 'list' | 'pipeline';
  preference_deals_pipeline_view?: 'list' | 'pipeline';
  preference_tickets_pipeline_view?: 'list' | 'pipeline';
  preference_current_organization_id?: number | null;
  preference_community_announcements_last_seen_at?: string | null;
  preference_activities_view?: 'list' | 'calendar';
  preference_nova_floating_voice_enabled?: boolean;
  preference_nova_floating_voice_position?: 'left' | 'center' | 'right';
  preference_nova_floating_voice_custom_left?: number | null;
  preference_nova_floating_voice_custom_bottom?: number | null;
  preferred_locale?: 'en' | 'es';
}

interface UserContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  loginWithOtp: (email: string, otp: string) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User>;
  updateUserPreference: (keyOrData: string | Record<string, unknown>, value?: unknown) => Promise<void>;
  getCurrentOrganization: (orgId: number) => Organization | undefined;
  getOrganizationRole: (orgId: number) => UserRole | undefined;
  getOrganizationPermissions: (orgId: number) => OrganizationPermissions | undefined;
  hasPermission: (orgId: number, permission: string) => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const reAuthenticate = async () => {
      try {
        const restAuth = await restFeathers.reAuthenticate();
        await socketFeathers.authenticate({
          strategy: 'jwt',
          accessToken: restAuth.accessToken,
        });
        setUser(restAuth.user);
      } catch (error) {
        console.error('Re-authentication failed:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    reAuthenticate();
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    try {
      console.log('[Login:Process] Starting login process...');
      
      // Authenticate with Feathers REST client
      console.log('[Login:Process] Authenticating with REST client...');
      const restAuth = await restFeathers.authenticate({
        strategy: 'local',
        email,
        password,
      });
      console.log('[Login:Process] REST authentication successful:', restAuth);

      // Set the user immediately after REST auth succeeds
      const user = restAuth.user as User;
      setUser(user);

      // Attempt socket authentication in the background
      socketFeathers.authenticate({
        strategy: 'jwt',
        accessToken: restAuth.accessToken,
      }).catch(error => {
        // Log socket authentication error but don't fail the login
        console.warn('[Login:Process] Socket authentication failed:', error);
      });

      return user;
    } catch (error: any) {
      console.error('[Login:Error] Login failed with details:', error);
      
      const statusCode = error.response?.status || error.code;
      
      if (statusCode === 500 || statusCode === 503) {
        console.log('[Login:Error] Server error (500/503) detected, clearing auth and refreshing');
        if (typeof window !== 'undefined') {
          localStorage.clear();
          document.cookie.split(";").forEach((c) => {
            const eqPos = c.indexOf("=");
            const name = eqPos > -1 ? c.substr(0, eqPos) : c;
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=." + window.location.hostname;
          });
          window.location.reload();
        }
        throw error;
      }
      
      throw error;
    }
  };

  const loginWithOtp = async (email: string, otp: string): Promise<User> => {
    try {
      console.log('[Login:OTP] Authenticating with email code…');
      const restAuth = await restFeathers.authenticate({
        strategy: 'otp',
        email,
        otp,
      });
      const authenticatedUser = restAuth.user as User;
      setUser(authenticatedUser);

      socketFeathers.authenticate({
        strategy: 'jwt',
        accessToken: restAuth.accessToken,
      }).catch(error => {
        console.warn('[Login:OTP] Socket authentication failed:', error);
      });

      return authenticatedUser;
    } catch (error: any) {
      console.error('[Login:OTP] Authentication failed:', error);

      const statusCode = error.response?.status || error.code;

      if (statusCode === 500 || statusCode === 503) {
        if (typeof window !== 'undefined') {
          localStorage.clear();
          document.cookie.split(";").forEach((c) => {
            const eqPos = c.indexOf("=");
            const name = eqPos > -1 ? c.substr(0, eqPos) : c;
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=." + window.location.hostname;
          });
          window.location.reload();
        }
        throw error;
      }

      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log('Starting logout process...');
      
      // Clear user state first
      setUser(null);
      
      // Clear any stored tokens
      // if (typeof window !== 'undefined') {
      //   localStorage.removeItem('feathers-jwt');
      // }
      
      // Attempt to logout from both clients, but don't wait for them
      Promise.allSettled([
        restFeathers.authentication.removeAccessToken(),
        socketFeathers.authentication.removeAccessToken()
      ]).then(results => {
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.warn(`Logout failed for client ${index}:`, result.reason);
          }
          window.location.href = '/login';
        });
      });
      
      console.log('Logout completed');
    } catch (error) {
      console.error('Logout error:', error);
      // Don't throw the error, as we've already cleared the local state
    }
  };

  const refreshUser = async () => {
    try {
      console.log('[UserContext] Refreshing user data...');
      const restAuth = await restFeathers.reAuthenticate();
      await socketFeathers.authenticate({
        strategy: 'jwt',
        accessToken: restAuth.accessToken,
      });
      setUser(restAuth.user);
      console.log('[UserContext] User data refreshed successfully');
      return restAuth.user;
    } catch (error) {
      console.error('[UserContext] Failed to refresh user data:', error);
      throw error;
    }
  };

  const updateUserPreference = async (keyOrData: string | Record<string, unknown>, value?: unknown) => {
    try {
      if (!user) {
        console.warn('Cannot update preference: user not logged in');
        return;
      }

      const patch = typeof keyOrData === 'string' ? { [keyOrData]: value } : keyOrData;

      console.log('[UserContext] Updating user preference(s):', patch);

      await restFeathers.service('users').patch(user.id, patch);

      setUser((prevUser) => (prevUser ? { ...prevUser, ...patch } as User : null));

      console.log('[UserContext] User preference(s) updated successfully');
    } catch (error) {
      console.error('Failed to update user preference:', error);
      throw error;
    }
  };

  const getCurrentOrganization = (orgId: number): Organization | undefined => {
    return user?.organizations?.find(org => org.id === orgId);
  };

  const getOrganizationRole = (orgId: number): UserRole | undefined => {
    const org = user?.organizations?.find(org => org.id === orgId);
    return org?.role;
  };

  const getOrganizationPermissions = (orgId: number): OrganizationPermissions | undefined => {
    const org = user?.organizations?.find(org => org.id === orgId);
    return org?.permissions;
  };

  const hasPermission = (orgId: number, permission: string): boolean => {
    const role = getOrganizationRole(orgId);
    
    // Only members have permission restrictions
    // Owners and admins always have all permissions
    if (role && hasUnrestrictedAccess(role)) {
      return true;
    }

    // For members, check their specific permissions
    const permissions = getOrganizationPermissions(orgId);
    
    // If permissions not loaded yet, default to true for better UX
    // (prevents flickering/blocking during initial load)
    if (!permissions) {
      return true;
    }

    // Check if the specific permission exists and is not explicitly false
    return permissions[permission] !== false;
  };

  return (
    <UserContext.Provider value={{ 
      user, 
      setUser, 
      isLoading, 
      login,
      loginWithOtp,
      logout,
      refreshUser,
      updateUserPreference,
      getCurrentOrganization,
      getOrganizationRole,
      getOrganizationPermissions,
      hasPermission
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
